import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { List, Spin, Button } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import useQueryApiClient from 'utils/useQueryApiClient';
import DrawerNotification, {
  DatabaseNotification,
  NotificationData,
} from 'ui/Drawer/Drawers/DrawerNotification';
import DrawerNotificationPreferences from 'ui/Drawer/Drawers/DrawerNotificationPreferences';
import NotificationTypeTag from '../../../admin/components/NotificationTypeTag';
import { StyledBellNotificationsDrawer } from './style';
import { useRealtimeFarmNotifications } from 'realtime/useRealtimeFarmNotifications';
import { useAuth } from 'hooks/useAuth';
import { adminPath } from 'enums/adminRoutes';

type Row = DatabaseNotification & { is_read?: boolean };

interface Props {
  open: boolean;
  onClose: () => void;
  onHasUnopenedChange?: (has: boolean) => void;
  onNotificationViewed?: () => void;
  pageSize?: number;
  title?: string;
}

function BellNotificationsDrawer({
  open,
  onClose,
  onHasUnopenedChange,
  onNotificationViewed,
  pageSize = 20,
}: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());

  const farmId = user?.farmId ?? user?.farm?.id;

  const feedRequest = useMemo(
    () => ({
      url: '/api/v2/notifications',
      data: {
        page: 1,
        page_size: pageSize,
        filter: { status: 'unopened', archived: 'active' },
      },
      disableOnMount: true,
    }),
    [pageSize]
  );

  const {
    refetch: refetchNotifs,
    isLoading,
    data: feedResponse,
  } = useQueryApiClient({
    request: feedRequest,
  });

  const { appendData: markViewed } = useQueryApiClient({
    request: { url: '/api/v2/notifications/:id/viewed', method: 'POST' },
    onSuccess: (_res, passOnSuccess) => {
      const id = passOnSuccess?.id;
      if (id) {
        setSelected((prev) =>
          prev && prev.id === id
            ? { ...prev, is_read: true }
            : prev
        );
      }
    },
  });

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      refetchNotifs();
    }
    prevOpenRef.current = open;
  }, [open, refetchNotifs]);

  useEffect(() => {
    setViewedIds(new Set());
  }, [feedResponse]);

  useEffect(() => {
    if (!notifDrawerOpen || !selected) return;
    const rows = Array.isArray((feedResponse as any)?.data)
      ? (feedResponse as any).data
      : [];
    const updated = rows.find((r: Row) => r.id === selected.id);
    if (updated) {
      setSelected(updated);
    }
  }, [feedResponse, notifDrawerOpen, selected?.id]);

  const unopened: Row[] = useMemo(() => {
    const rows = Array.isArray((feedResponse as any)?.data)
      ? (feedResponse as any).data
      : [];
    return rows.filter((r: Row) => !viewedIds.has(r.id));
  }, [feedResponse, viewedIds]);

  useEffect(() => {
    onHasUnopenedChange?.(unopened.length > 0);
  }, [unopened.length, onHasUnopenedChange]);

  const handleRealtimeEvent = useCallback(() => {
    if (open) {
      refetchNotifs();
    }
  }, [open, refetchNotifs]);

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.farm.notification',
    onEvent: handleRealtimeEvent,
  });

  useRealtimeFarmNotifications({
    farmId,
    eventName: '.notification.updated',
    onEvent: handleRealtimeEvent,
  });

  const refetchAndUpdateSelected = useCallback(
    (updates: Partial<Row>) => {
      refetchNotifs();
      setSelected((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    [refetchNotifs]
  );

  const openNotification = useCallback(
    (row: Row) => {
      setSelected(row);
      setNotifDrawerOpen(true);

      if (row?.is_read === false) {
        setViewedIds((prev) => new Set(prev).add(row.id));
        markViewed(null, { id: row.id }, { id: row.id });
        onNotificationViewed?.();
      }
    },
    [markViewed, onNotificationViewed]
  );

  const renderItem = useCallback(
    (item: Row) => {
      const payload: NotificationData = item.data as NotificationData;
      const titleText = payload?.title?.trim?.() ? payload.title : '';
      const ts = item?.created_at
        ? dayjs(item.created_at).format('DD.MM.YYYY | HH:mm')
        : '-';
      const shortType = payload?.type;

      return (
        <List.Item
          key={item.id}
          className="item"
          onClick={() => openNotification(item)}
        >
          <div className="meta">
            <span>{ts}</span>
            <div className="tag">
              <NotificationTypeTag
                shortType={shortType}
                fallbackType={item?.type}
                size="md"
              />
            </div>
          </div>
          <div className="title">{titleText}</div>
        </List.Item>
      );
    },
    [openNotification]
  );

  return (
    <>
      <StyledBellNotificationsDrawer
        title={intl.formatMessage({ id: 'notifications.belldrawer.title' })}
        closeIcon={
          <div className="bell-header-actions">
            <button
              className="bell-settings-btn"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPrefsOpen(true);
              }}
            >
              <img
                src="/svgs/admin/default-admin-icon.svg"
                width={20}
                height={20}
                alt=""
              />
            </button>
            <span className="bell-close-icon">&#x2715;</span>
          </div>
        }
        placement="right"
        width={923}
        open={open}
        onClose={onClose}
        footer={
          <div className="drawer-footer">
            <Button
              onClick={() => {
                onClose();
                navigate(adminPath.notifications());
              }}
            >
              {intl.formatMessage({
                id: 'notifications.see.all.notifications',
              })}
            </Button>
          </div>
        }
      >
        {isLoading ? (
          <div className="list-loading">
            <Spin />
          </div>
        ) : unopened.length === 0 ? (
          <div className="empty">
            {intl.formatMessage({ id: 'notifications.no.notifications' })}
          </div>
        ) : (
          <List
            itemLayout="vertical"
            dataSource={unopened}
            renderItem={renderItem}
          />
        )}
      </StyledBellNotificationsDrawer>

      <DrawerNotification
        open={notifDrawerOpen}
        notification={selected as DatabaseNotification | null}
        onClose={() => setNotifDrawerOpen(false)}
        onMarkedRead={() =>
          refetchAndUpdateSelected({
            read_at: new Date().toISOString(),
            is_read: true,
          })
        }
        onMarkedUnread={() =>
          refetchAndUpdateSelected({ read_at: null, is_read: false })
        }
        onArchived={() =>
          refetchAndUpdateSelected({ archived_at: new Date().toISOString() })
        }
        onUnarchived={() => refetchAndUpdateSelected({ archived_at: null })}
      />

      <DrawerNotificationPreferences
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
      />
    </>
  );
}

export default BellNotificationsDrawer;
